import React from 'react';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import {
  formatClubAcreage,
  formatClubDistance,
  formatClubDues,
  formatClubOpenings,
  formatClubStandCount,
} from '../utils/findClubDisplayUtils';
import { FindClubCardActions } from './FindClubCardActions';
import './FindClubCard.css';

export function FindClubCard({ club, highlighted = false, variant = 'compact' }) {
  const dues = formatClubDues(club.annual_dues);
  const distance = formatClubDistance(club.distance_miles);
  const acreage = formatClubAcreage(club.acreage);
  const stands = formatClubStandCount(club.stand_count);
  const openings = formatClubOpenings(club.openings, club.max_members, club.member_count);
  const statusLabel = club.is_accepting_members ? 'Accepting' : 'Waitlist';

  if (variant === 'row') {
    const metaBits = [
      club.location_label,
      distance,
      acreage,
      stands ? `${club.stand_count} STANDS` : null,
    ].filter(Boolean);

    return (
      <article
        className={`find-club-card find-club-card--row${highlighted ? ' find-club-card--highlighted' : ''}`}
        data-testid={`find-club-card-${club.id}`}
      >
        <div className="find-club-card__thumb">
          {club.logo_url ? (
            <img src={club.logo_url} alt="" />
          ) : (
            <div className="find-club-card__thumb-placeholder" aria-hidden />
          )}
        </div>

        <div className="find-club-card__row-main">
          <h3 className="find-club-card__title">{club.name}</h3>
          {metaBits.length > 0 ? (
            <div className="find-club-card__meta">{metaBits.join(' · ')}</div>
          ) : null}
        </div>

        {dues ? (
          <div className="find-club-card__dues" aria-label={`Dues ${dues}`}>
            <span className="find-club-card__dues-value">{dues.replace(/\/yr$/i, '').trim()}</span>
            <span className="find-club-card__dues-unit">/YR</span>
          </div>
        ) : null}

        <span
          className={`find-club-card__badge ${
            club.is_accepting_members
              ? 'find-club-card__badge--accepting'
              : 'find-club-card__badge--waitlist'
          }`}
        >
          {statusLabel}
        </span>

        <FindClubCardActions club={club} layout="inline" />
      </article>
    );
  }

  return (
    <article
      className={`find-club-card${highlighted ? ' find-club-card--highlighted' : ''}`}
      data-testid={`find-club-card-${club.id}`}
    >
      <div className="find-club-card__header">
        <div className="find-club-card__identity">
          {club.logo_url ? (
            <img className="find-club-card__logo" src={club.logo_url} alt="" />
          ) : (
            <div className="find-club-card__logo find-club-card__logo--placeholder" aria-hidden />
          )}
          <div>
            <div className="find-club-card__title-row">
              <h3 className="find-club-card__title">{club.name}</h3>
              {club.is_accepting_members ? (
                <span className="find-club-card__badge find-club-card__badge--accepting">Accepting</span>
              ) : (
                <span className="find-club-card__badge find-club-card__badge--waitlist">Waitlist</span>
              )}
            </div>
            {club.location_label ? (
              <div className="find-club-card__location">
                <FaMapMarkerAlt aria-hidden />
                <span>{club.location_label}</span>
                {distance ? <span className="find-club-card__distance">{distance}</span> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {club.listing_description ? (
        <p className="find-club-card__description">{club.listing_description}</p>
      ) : null}

      <div className="find-club-card__stats">
        {acreage ? <span>{acreage}</span> : null}
        {stands ? <span>{stands}</span> : null}
        {typeof club.member_count === 'number' ? (
          <span>
            <FaUsers aria-hidden /> {club.member_count}
            {club.max_members ? ` / ${club.max_members}` : ''}
          </span>
        ) : null}
        {openings ? <span>{openings}</span> : null}
        {dues ? <span>{dues}</span> : null}
        {club.camphouse_label ? <span>{club.camphouse_label}</span> : null}
      </div>

      {club.president_name ? (
        <div className="find-club-card__footer">
          <span className="find-club-card__president">{club.president_name}, President</span>
        </div>
      ) : null}

      <FindClubCardActions club={club} layout="stacked" />
    </article>
  );
}

FindClubCard.propTypes = {
  club: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    listing_description: PropTypes.string,
    location_label: PropTypes.string,
    logo_url: PropTypes.string,
    distance_miles: PropTypes.number,
    acreage: PropTypes.number,
    stand_count: PropTypes.number,
    member_count: PropTypes.number,
    max_members: PropTypes.number,
    openings: PropTypes.number,
    annual_dues: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    camphouse_label: PropTypes.string,
    is_accepting_members: PropTypes.bool,
    is_promoted: PropTypes.bool,
    require_approval_to_join: PropTypes.bool,
    president_name: PropTypes.string,
    president_id: PropTypes.number,
  }).isRequired,
  highlighted: PropTypes.bool,
  variant: PropTypes.oneOf(['compact', 'row']),
};
